import { memo, MouseEvent } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconSms } from '@components/icons/phone-sms';
import { Stack } from '@components/stack';
import { StatusIndicator, StatusIndicatorGroup } from '@components/status-indicator';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import { useJourneyAction } from 'api/journey-actions/hooks';
import { JourneyActionValuesTypes } from 'api/types';
import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { useJourneyContext } from 'ui/journey-builder/base-builder';
import { NodeActions } from 'ui/journey-builder/node-types/common';
import { JourneyNodeActionCard, NodeContainer } from 'ui/journey-builder/node-types/node-container';
import { TriggerMethodIndicator } from 'ui/journey-builder/node-types/trigger-method-indicator';
import { JourneyNode } from 'ui/journey-builder/node-types/types';
import { isInTemplateRoute } from 'ui/router';

import { useEditJourneyActionFormOverlay } from './edit-journey-action-form-overlay';
import { useOverlayPreview } from './overlay-preview';

import { buildEmailPreviewProps } from '../send-email/email-preview';
import { PreviewEmailMenuItemView } from '../send-email/preview-email-menu-item';
import { PreviewSmsMenuItemView } from '../send-sms/preview-sms-menu-item';
import { buildSmsPreviewProps } from '../send-sms/sms-preview';

const ACTION_TYPE = ACTION_TYPES.combo_send_form;

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  const { t } = useScopedTranslate(nodeLocale);
  const { journeyActionForms } = useJourneyContext();
  const showEditJourneyActionFormOverlay = useEditJourneyActionFormOverlay();
  const showOverlayPreview = useOverlayPreview();
  const isPreviewOnly = isInTemplateRoute();
  const journeyActionForm = journeyActionForms[node.id];

  const formName = journeyActionForm?.name ?? t('untitledForm');
  const formId = journeyActionForm?.shortId;
  const hasEmailEnabled = node?.data?.values?.emailNotify;
  const hasSmsEnabled = node?.data?.values?.smsNotify;

  const handleEditClick = () => {
    if (formId) {
      showEditJourneyActionFormOverlay({ formId });
    }
  };

  const handlePreviewClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (formId) {
      showOverlayPreview({
        formId,
      });
    }
  };

  return (
    <NodeContainer node={node}>
      <JourneyNodeActionCard
        node={node}
        label={getActionLabel(ACTION_TYPE)}
        renderBelowDescription={
          <div style={{ paddingTop: pxToRem(8), paddingRight: pxToRem(8) }}>
            <Card spaceY={2} spaceX={4}>
              <Stack align="center" orientation="horizontal" justify="space-between">
                <Stack align="center" space={3} orientation="horizontal" style={{ minWidth: 0 }}>
                  <IconForm />
                  <Tooltip content={formName}>
                    <NameContainer>
                      <Text font="semibold14" text={formName ?? t('untitledForm')} />
                    </NameContainer>
                  </Tooltip>
                </Stack>
                <ButtonWrapper>
                  {isPreviewOnly ? (
                    <Button
                      className="template-node-preview-button"
                      variant="ghost"
                      dataTestid="preview-form"
                      onClick={handlePreviewClick}
                      text={t('core.preview')}
                    />
                  ) : (
                    <Button
                      variant="ghost"
                      dataTestid="edit-form"
                      onClick={handleEditClick}
                      text={t('core.edit')}
                    />
                  )}
                </ButtonWrapper>
              </Stack>
            </Card>
          </div>
        }
      >
        <NodeActions
          node={node}
          customExecutionIndicators={
            <StatusIndicatorGroup>
              {hasEmailEnabled && (
                <StatusIndicator variant="notStarted" icon={<IconEmail />} label={t('email')} />
              )}
              {hasSmsEnabled && (
                <StatusIndicator variant="notStarted" icon={<IconSms />} label={t('sms')} />
              )}
              <TriggerMethodIndicator
                isAutomatic={node.data?.isAutomatic}
                assignees={node.data?.assignees}
              />
            </StatusIndicatorGroup>
          }
          customMenuItems={
            hasEmailEnabled || hasSmsEnabled ? (
              <>
                {/* Each channel previews independently (like the sidepanel): a
                    broken variable on one must not disable the other, and a broken
                    action is what you'd open the preview to inspect. */}
                {hasEmailEnabled && <PreviewEmailMenuItem actionId={node.id} />}
                {hasSmsEnabled && <PreviewSmsMenuItem actionId={node.id} />}
              </>
            ) : null
          }
        />
      </JourneyNodeActionCard>
    </NodeContainer>
  );
});

type PreviewMenuItemProps = {
  actionId: string;
};

const PreviewEmailMenuItem = ({ actionId }: PreviewMenuItemProps) => {
  const { t } = useScopedTranslate(nodeLocale);
  const { data: journeyActionData } = useJourneyAction<typeof ACTION_TYPE>(actionId);

  const sendEmailGroupAction = journeyActionData?.groupActions?.find(
    (groupAction) => groupAction.action === 'send_email'
  );
  // The form action stores its email config under the send_email sub-action
  // using the same shape as the standalone action, so it reuses that builder.
  const sendEmailValues = sendEmailGroupAction?.values as
    | JourneyActionValuesTypes['send_email']
    | undefined;

  return (
    <PreviewEmailMenuItemView
      previewProps={
        sendEmailGroupAction
          ? buildEmailPreviewProps(
              sendEmailValues,
              sendEmailGroupAction.shortId,
              t('noEmailRecipient'),
              'journey'
            )
          : undefined
      }
    />
  );
};

const PreviewSmsMenuItem = ({ actionId }: PreviewMenuItemProps) => {
  const { t } = useScopedTranslate(nodeLocale);
  const { data: journeyActionData } = useJourneyAction<typeof ACTION_TYPE>(actionId);

  const sendSmsGroupAction = journeyActionData?.groupActions?.find(
    (groupAction) => groupAction.action === 'send_sms'
  );
  const sendSmsValues = sendSmsGroupAction?.values as
    | JourneyActionValuesTypes['send_sms']
    | undefined;

  return (
    <PreviewSmsMenuItemView
      previewProps={
        sendSmsGroupAction
          ? buildSmsPreviewProps(sendSmsValues, sendSmsGroupAction.shortId, t('noRecipient'))
          : undefined
      }
    />
  );
};

const nodeLocale = {
  en: {
    untitledForm: 'Untitled form',
    noRecipient: 'SMS has no recipients',
    noEmailRecipient: 'Email has no recipients',
    email: 'Email',
    sms: 'SMS',
  },
};

const NameContainer = styled.div({
  flexGrow: 1,
  minWidth: 0,
  ...lineClamp('-webkit-box', 2, true),
  wordBreak: 'normal',
});

const ButtonWrapper = styled.div({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});
